import React, { useEffect, useState } from 'react';
import DataTable from '../../components/tables/DataTable';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import StatCard from '../../components/charts/StatCard';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import Toast from '../../components/common/Toast';
import { useToast } from '../../hooks/useToast';
import managerService from '../../services/managerService';

import AddManagerModal from '../../components/features/managers/AddManagerModal';
import EditManagerModal from '../../components/features/managers/EditManagerModal';

const ManagersView = () => {
  const [managers, setManagers] = useState([]);
  const [filteredManagers, setFilteredManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedManager, setSelectedManager] = useState(null);

  const { toasts, removeToast, success, error } = useToast();

  useEffect(() => {
    loadManagers();
  }, []);

  useEffect(() => {
    filterManagers();
  }, [searchTerm, managers]);

  const loadManagers = async () => {
    setLoading(true);
    const result = await managerService.getAllManagers();
    if (result.success) {
      setManagers(result.data);
      setFilteredManagers(result.data);
    } else {
      error(result.error || 'Failed to load Managers');
    }
    setLoading(false);
  };

  const filterManagers = () => {
    let filtered = [...managers];

    if (searchTerm) {
      filtered = filtered.filter(m =>
        m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredManagers(filtered);
  };

  const handleAddManager = async (managerData) => {
    const result = await managerService.createManager(managerData);
    if (result.success) {
      success('Manager added successfully!');
      await loadManagers();
    } else {
      error(result.error || 'Failed to add Manager');
    }
  };

  const handleEditManager = (manager) => {
    setSelectedManager(manager);
    setShowEditModal(true);
  };

  const handleUpdateManager = async (managerId, managerData) => {
    const result = await managerService.updateManager(managerId, managerData);
    if (result.success) {
      success('Manager updated successfully!');
      await loadManagers();
    } else {
      error(result.error || 'Failed to update Manager');
    }
  };

  const handleDeleteManager = (manager) => {
    setSelectedManager(manager);
    setShowDeleteDialog(true);
  };

  const handleDeleteConfirm = async () => {
    const result = await managerService.deleteManager(selectedManager.id);
    if (result.success) {
      success('Manager deleted successfully!');
      await loadManagers();
    } else {
      error(result.error || 'Failed to delete Manager');
    }
    setShowDeleteDialog(false);
    setSelectedManager(null);
  };

  const getStats = () => {
    const total = managers.length;
    const active = managers.filter(m => m.is_active).length;
    return { total, active };
  };

  const stats = getStats();

  const filterBarStyle = {
    background: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '12px',
    padding: '20px',
    marginBottom: '24px',
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
    alignItems: 'flex-end',
    justifyContent: 'space-between'
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '20px',
    marginBottom: '32px'
  };

  const columns = [
    {
      key: 'name',
      label: 'Manager',
      render: (value, row) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: 'var(--primary)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: '700',
            fontSize: '16px'
          }}>
            {value.charAt(0)}
          </div>
          <div>
            <div style={{ fontWeight: '600' }}>{value}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {row.email}
            </div>
          </div>
        </div>
      )
    },
    {
      key: 'contact',
      label: 'Contact'
    },
    {
      key: 'engineers_count',
      label: 'Team Size',
      render: (value) => (
        <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>
          {value || 0}
        </span>
      )
    },
    {
        key: 'status',
        label: 'Status',
        render: (value) => (
          <span style={{
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: '600',
            background: value === 'Active' ? '#10b98120' : '#6b728020',
            color: value === 'Active' ? '#10b981' : '#6b7280'
          }}>
            {value}
          </span>
        )
      }
  ];

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-secondary)' }}>Loading Managers...</div>;
  }

  return (
    <div>
      {toasts.map(toast => (
        <Toast key={toast.id} message={toast.message} type={toast.type} onClose={() => removeToast(toast.id)} />
      ))}

      <div style={gridStyle}>
        <StatCard title="Total Managers" value={stats.total} icon="" color="#3b82f6" />
        <StatCard title="Active Managers" value={stats.active} icon="" color="#10b981" />
      </div>

      <div style={filterBarStyle}>
        <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
          <Input
            placeholder="Search managers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.35-4.35"/>
              </svg>
            }
          />
        </div>
        <div style={{ display: 'flex', gap: '12px' }}>
          <Button variant="secondary" onClick={loadManagers}>Refresh</Button>
          <Button variant="primary" onClick={() => setShowAddModal(true)}>Add Manager</Button>
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredManagers}
        actions={[
          { label: ' Edit', onClick: handleEditManager },
          { label: ' Delete', onClick: handleDeleteManager }
        ]}
        pageSize={15}
      />

      <AddManagerModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddManager}
      />

      <EditManagerModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedManager(null);
        }}
        onSubmit={handleUpdateManager}
        manager={selectedManager}
      />

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => {
          setShowDeleteDialog(false);
          setSelectedManager(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Manager"
        message={`Are you sure you want to deactivate Manager ${selectedManager?.name}? They will no longer be able to log in or manage their team.`}
        confirmText="Delete"
        type="danger"
      />
    </div>
  );
};

export default ManagersView;
